import { Drawer } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { JupiterTheme } from '../../../theme/types';
import ConfirmationPrompt from '../../Modals/ConfirmationPrompt';
import RawDataModal from '../../Modals/RawData/RawData';
import LogoProgress from '../../progress/LogoProgress';
import CreateEntityModal from './CreateEntity/CreateEntityModal';
import EntityDrawer from './EntityDrawer';
import useEntityDrawer, {
  EntityDrawerPropsApollo,
  EntityDrawerPropsStatic,
  EntityDrawerProvider,
  PromptMode,
} from './useEntityDrawer';

export const ENTITY_DRAWER_DEFAULT_WIDTH = 400;
const EDIT_MESSAGE =
  'You are about to edit an entity imported from an integration. It is recommended to make changes at the data source, if possible.';
const DELETE_MESSAGE =
  'This entity was imported from an integration, deletion of this entity may be overridden the next time the integration runs.';

const useStyles = makeStyles((theme: JupiterTheme) => {
  const palette = theme.palette;

  return createStyles({
    positionDock: {
      width: ENTITY_DRAWER_DEFAULT_WIDTH,
      background: palette.common.white,
      boxShadow: `inset 1px 0 ${palette.grey[300]}`,
      position: 'absolute',
      right: '0px',
      top: '0px',
      bottom: '0px',
    },
    drawerPaper: {
      background: palette.common.white,
      boxShadow: 'none',
      flexFlow: 'column',
      overflowX: 'hidden',
      overflowY: 'auto',
      position: 'relative',
      width: ENTITY_DRAWER_DEFAULT_WIDTH,
      zIndex: 3,
      '& > svg': {
        width: 60,
        height: 60,
      },
    },
    drawerTitleBar: {
      display: 'flex',
      flex: '1 0 auto',
      justifyContent: 'space-between',
    },
    docked: {
      top: theme.spacing(7),
      width: 400,
      background: theme.palette.common.white,
      boxShadow: `inset 1px 0 ${theme.palette.grey[300]}`,
      position: 'absolute',
      right: '0px',
      bottom: '0px',
    },
    notDocked: {
      display: 'none',
    },
  });
});

export const EntityDrawerContainer = () => {
  const classes = useStyles();
  const {
    isOpen,
    editModal,
    deleteModal,
    rawDataModal,
    promptMode,
    entity: { _legacyEntity, hasError, loading, updateEntity, deleteEntity },
    isDocked,
    onClose,
    setPromptMode,
  } = useEntityDrawer();

  useEffect(() => {
    if (hasError) onClose?.();
  }, [hasError]);

  return (
    <>
      <Drawer
        data-testid="EntityDrawerWrapper"
        variant="persistent"
        anchor="right"
        open={isOpen}
        classes={{
          paper: classes.drawerPaper,
          docked: isOpen
            ? clsx(isDocked ? classes.docked : classes.positionDock)
            : classes.notDocked,
        }}
      >
        {loading || !isOpen ? <LogoProgress /> : <EntityDrawer />}
      </Drawer>
      {editModal && editModal?.state && (
        <CreateEntityModal
          open={editModal.state}
          handleClose={editModal.close}
          onUpdateEntity={updateEntity}
          mode={'edit'}
          entity={_legacyEntity}
        />
      )}
      {deleteModal && deleteModal?.state && (
        <ConfirmationPrompt
          variant="delete"
          open={deleteModal.state}
          message="Are you sure you want to delete?"
          onConfirm={deleteEntity}
          onClose={deleteModal.close}
        />
      )}
      {_legacyEntity && rawDataModal?.state && (
        <RawDataModal
          open={rawDataModal.state}
          handleClose={rawDataModal.close}
          entity={_legacyEntity}
        />
      )}
      {(deleteModal || editModal) && promptMode !== PromptMode.Closed && (
        <ConfirmationPrompt
          data-testid="edit-prompt"
          open
          message={
            promptMode === PromptMode.Delete ? DELETE_MESSAGE : EDIT_MESSAGE
          }
          onConfirm={
            promptMode === PromptMode.Delete
              ? deleteModal!.open
              : editModal!.open
          }
          onClose={() => setPromptMode(PromptMode.Closed)}
        />
      )}
    </>
  );
};

type Props = EntityDrawerPropsApollo & EntityDrawerPropsStatic;
const EntityDrawerContainerWrapper = (props: Props) => {
  return (
    <EntityDrawerProvider {...props}>
      <EntityDrawerContainer />
    </EntityDrawerProvider>
  );
};
export default EntityDrawerContainerWrapper;
